import { router } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { Sparkles, Play, Edit, Power, Trash2, History } from "lucide-react";
import { cn } from "@/lib/utils";
import { useConfirm } from "@/hooks/useConfirm";

export function CelebrationsTab({ celebration_events = [] }) {
  const { confirm, ConfirmDialog } = useConfirm();

  const handleToggleActive = async (eventId, isActive) => {
    const confirmed = await confirm({
      title: isActive ? 'Pausar evento' : 'Activar evento',
      message: `¿Estás seguro de ${isActive ? 'pausar' : 'activar'} este evento?`,
      confirmText: isActive ? 'Pausar' : 'Activar',
      variant: isActive ? 'warning' : 'success'
    });

    if (!confirmed) return;

    router.post(`/admin/celebration_events/${eventId}/toggle_active`, {}, {
      preserveScroll: true
    });
  };

  const handleProcessNow = async (eventId) => {
    const confirmed = await confirm({
      title: 'Ejecutar evento manualmente',
      message: '¿Estás seguro de ejecutar este evento manualmente? Esto procesará inmediatamente todos los usuarios elegibles.',
      confirmText: 'Ejecutar ahora',
      variant: 'info'
    });

    if (!confirmed) return;

    router.post(`/admin/celebration_events/${eventId}/process_now`, {}, {
      preserveScroll: true
    });
  };

  const handleEdit = (eventId) => {
    router.visit(`/admin/celebration_events/${eventId}/edit`);
  };

  const handleDelete = async (eventId, eventName) => {
    const confirmed = await confirm({
      title: 'Eliminar evento',
      message: `¿Estás seguro de eliminar el evento "${eventName}"? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger'
    });

    if (!confirmed) return;

    router.delete(`/admin/celebration_events/${eventId}`, {
      preserveScroll: true
    });
  };

  const getEventEmoji = (triggerType) => {
    const emojis = {
      birthday: '🎂',
      work_anniversary: '🎉',
      new_user: '👋',
      custom_date: '📅'
    };
    return emojis[triggerType] || '🎁';
  };

  return (
    <Card className="border-t-4 border-t-purple-500 shadow-sm animate-in fade-in slide-in-from-bottom-2">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center text-xl">
              <Sparkles className="w-5 h-5 mr-2 text-purple-500" />
              Celebraciones Automáticas
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Eventos que otorgan puntos automáticamente. Se ejecutan diariamente a las 8:00 AM.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => router.visit('/admin/celebration_awards')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              size="sm"
            >
              <History className="w-4 h-4 mr-2" />
              Ver historial
            </Button>
            <Button
              onClick={() => router.visit('/admin/celebration_events/new')}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              size="sm"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Nuevo evento
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {celebration_events.map((event) => (
            <Card key={event.id} className="shadow-md hover:shadow-lg transition-shadow border-l-4" style={{ borderLeftColor: event.active ? '#10B981' : '#9CA3AF' }}>
              <CardContent className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{getEventEmoji(event.trigger_type)}</span>
                    <div>
                      <h3 className="font-semibold text-gray-900">{event.name}</h3>
                      <p className="text-xs text-gray-500">{event.hashtag || `Trigger: ${event.trigger_type}`}</p>
                      {event.repeatable && <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Repetible</span>}
                    </div>
                  </div>
                  <Badge className={cn(
                    "text-[10px]",
                    event.active
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-700"
                  )}>
                    {event.active ? "✅ Activo" : "⏸️ Inactivo"}
                  </Badge>
                </div>

                {/* Points */}
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg p-3 mb-3 border border-orange-200">
                  <div className="text-xs text-orange-600 font-medium mb-1">Puntos a Otorgar</div>
                  <div className="text-xl font-bold text-orange-700">{event.points_amount} pts</div>
                </div>

                {/* Message Preview */}
                <div className="bg-gray-50 rounded-lg p-2 mb-3 border border-gray-200">
                  <div className="text-[10px] text-gray-600 font-medium mb-1">Mensaje</div>
                  <div className="text-xs text-gray-700 italic line-clamp-2">
                    "{event.message_template}"
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(event.id)}
                      className="flex-1 h-8 text-xs text-blue-600 hover:bg-blue-50"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Editar
                    </Button>

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleToggleActive(event.id, event.active)}
                      className={cn(
                        "flex-1 h-8 text-xs",
                        event.active
                          ? "text-yellow-600 hover:bg-yellow-50"
                          : "text-green-600 hover:bg-green-50"
                      )}
                    >
                      <Power className="w-3 h-3 mr-1" />
                      {event.active ? 'Pausar' : 'Activar'}
                    </Button>

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleProcessNow(event.id)}
                      className="flex-1 h-8 text-xs text-purple-600 hover:bg-purple-50"
                      disabled={!event.active}
                    >
                      <Play className="w-3 h-3 mr-1" />
                      Ejecutar
                    </Button>
                  </div>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(event.id, event.name)}
                    className="w-full h-8 text-xs text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Eliminar Evento
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {celebration_events.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No hay eventos de celebración configurados</p>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            ¿Cómo funcionan las celebraciones?
          </h4>
          <ul className="text-xs text-blue-800 space-y-1">
            <li>• <strong>Automáticas:</strong> Se ejecutan cada día a las 8:00 AM</li>
            <li>• <strong>Sin saldo:</strong> El sistema otorga puntos ilimitadamente</li>
            <li>• <strong>Deduplicación:</strong> Cada usuario recibe la celebración una sola vez por fecha</li>
            <li>• <strong>Notificaciones:</strong> Se envía webhook a Basecamp automáticamente</li>
            <li>• <strong>Ejecución manual:</strong> Usa el botón "Ejecutar" para testing</li>
          </ul>
        </div>
      </CardContent>
      <ConfirmDialog />
    </Card>
  );
}
